import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { RefreshCw } from "lucide-react";
import { Button, FeedbackState, Skeleton, type MetricChange } from "@h2j-data-team/ui";

import { KpiCell, KpiStrip } from "@/components/app-analysis/kpi";
import { reloadPerformanceQueryOptions } from "@/features/reloads/api";
import { useLocale } from "@/hooks/use-locale";
import type { ReloadPeriodParams } from "@/lib/api/reloads";
import { formatSeconds } from "@/lib/duration-format";
import { AppsView } from "./AppsView";
import { DailyDurationChart, DailyHoursChart } from "./charts";

const DAY_MS = 86_400_000;

// The period's hours, durations and wait against the window of the same length
// right before it, the days in between, and the apps that take the most hours.
export function PerformanceView({
  connectionId,
  baseUrl,
  period,
  version,
  onViewRuns,
}: {
  connectionId: string;
  baseUrl: string;
  period: ReloadPeriodParams;
  version: string;
  onViewRuns: (appId: string) => void;
}) {
  const { t } = useTranslation("reloads");
  const { t: tc } = useTranslation("common");
  const { language } = useLocale();
  const query = useQuery(reloadPerformanceQueryOptions(connectionId, period, version));
  const data = query.data;
  const summary = data?.summary;
  const previous = data?.previous ?? null;
  const periodDays = Math.round((Date.parse(period.to) - Date.parse(period.from)) / DAY_MS);

  const count = (value: number | undefined) =>
    value === undefined ? "—" : value.toLocaleString(language);
  const duration = (seconds: number | null | undefined) =>
    seconds === null || seconds === undefined ? "—" : formatSeconds(seconds);
  const hours = (seconds: number | undefined) =>
    seconds === undefined
      ? "—"
      : t("apps.hours", {
          value: (seconds / 3600).toLocaleString(language, {
            minimumFractionDigits: 1,
            maximumFractionDigits: 1,
          }),
        });

  // More hours, a longer run or a longer wait than the window before is worse.
  const change = (
    now: number | null | undefined,
    before: number | null | undefined,
  ): MetricChange | undefined => {
    if (now === null || now === undefined || !before) return undefined;
    const pct = ((now - before) / before) * 100;
    const direction = Math.abs(pct) < 0.5 ? "flat" : pct > 0 ? "up" : "down";
    return {
      value: `${Math.abs(pct).toLocaleString(language, { maximumFractionDigits: 0 })}%`,
      direction,
      tone: direction === "flat" ? "neutral" : direction === "up" ? "negative" : "positive",
    };
  };
  const vsPrevious = previous ? t("performance.kpi.vsPrevious", { count: periodDays }) : undefined;

  if (query.isError) {
    return (
      <FeedbackState
        state="error"
        title={t("error.load")}
        action={
          <Button variant="outline" onClick={() => void query.refetch()}>
            <RefreshCw />
            {tc("tryAgain")}
          </Button>
        }
      />
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <p className="text-xs text-dim">
        {t("performance.method")}
        {data && !previous && ` ${t("performance.noPrevious")}`}
      </p>

      <KpiStrip>
        <KpiCell
          label={t("performance.kpi.hours")}
          value={hours(summary?.totalSeconds)}
          change={change(summary?.totalSeconds, previous?.totalSeconds)}
          subMeta={vsPrevious}
        />
        <KpiCell
          label={t("performance.kpi.median")}
          value={duration(summary?.medianSeconds)}
          change={change(summary?.medianSeconds, previous?.medianSeconds)}
          subMeta={vsPrevious}
        />
        <KpiCell
          label="p90"
          value={duration(summary?.p90Seconds)}
          change={change(summary?.p90Seconds, previous?.p90Seconds)}
          subMeta={vsPrevious ?? t("performance.kpi.p90Hint")}
        />
        <KpiCell
          label={t("performance.kpi.finished")}
          value={count(summary?.finished)}
          subMeta={
            summary &&
            t("performance.kpi.finishedHint", {
              succeeded: count(summary.succeeded),
              failed: count(summary.failed),
            })
          }
        />
        <KpiCell
          label={t("performance.kpi.wait")}
          value={duration(summary?.medianWaitSeconds)}
          change={change(summary?.medianWaitSeconds, previous?.medianWaitSeconds)}
          subMeta={vsPrevious}
        />
      </KpiStrip>

      <div className="grid gap-4 lg:grid-cols-2">
        {data ? (
          <>
            <DailyHoursChart days={data.days} />
            <DailyDurationChart days={data.days} summary={data.summary} />
          </>
        ) : (
          <>
            <Skeleton className="h-72 w-full" />
            <Skeleton className="h-72 w-full" />
          </>
        )}
      </div>

      <section className="flex flex-col gap-2">
        <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
          <h2 className="text-sm font-semibold text-ink">{t("apps.title")}</h2>
          <span className="text-xs text-dim">{t("apps.note")}</span>
        </div>
        <AppsView
          connectionId={connectionId}
          baseUrl={baseUrl}
          period={period}
          version={version}
          totalSeconds={summary?.totalSeconds}
          onViewRuns={onViewRuns}
        />
      </section>
    </div>
  );
}
